import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendEmergencyNotification, getUserDirectory, getAllPremises, getAllEmergencyResponsePlans, getAllBCPPlans } from '../../../service/api';
import axiosInstance from '../../../common/axiosInstance';

const SendNotification = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<any>({
        title: '',
        message: '',
        priority: 'Medium',
        channels: [],
        targetType: 'All',
        targetRoles: [],
        targetLocations: [],
        targetUsers: [],
        linkedERPId: '',
        linkedBCPId: '',
        escalationEnabled: false,
        escalationTimeout: 30,
        escalateToUsers: []
    });

    const [options, setOptions] = useState<any>({
        users: [],
        roles: [],
        premises: [],
        erpPlans: [],
        bcpPlans: []
    });
    const [rolePickerValue, setRolePickerValue] = useState('');
    const [locationPickerValue, setLocationPickerValue] = useState('');
    const [userPickerValue, setUserPickerValue] = useState('');
    const [escalationUserPickerValue, setEscalationUserPickerValue] = useState('');

    useEffect(() => {
        fetchOptions();
    }, []);

    const fetchOptions = async () => {
        try {
            // Fetch roles using direct API call (axiosInstance already includes baseURL and auth headers)
            const rolesRes = await axiosInstance.get("/api/roles");

            const [usersRes, premisesRes, erpRes, bcpRes] = await Promise.all([
                getUserDirectory(),
                getAllPremises(),
                getAllEmergencyResponsePlans(),
                getAllBCPPlans()
            ]);

            // Extract roles with proper validation
            const roles = (rolesRes.data?.data && Array.isArray(rolesRes.data.data))
                ? rolesRes.data.data
                : [];

            // Extract users - handle both response.data.message and response.data.data
            const users = Array.isArray(usersRes.data?.data)
                ? usersRes.data.data
                : Array.isArray(usersRes.data?.message)
                ? usersRes.data.message
                : [];

            // Extract premises - getAllPremises returns response.data directly as array
            const premises = Array.isArray(premisesRes.data)
                ? premisesRes.data
                : Array.isArray(premisesRes.data?.data)
                ? premisesRes.data.data
                : [];

            // Extract ERP plans with proper validation
            const erpPlans = (erpRes.data?.resCode === 200 && erpRes.data?.data)
                ? (Array.isArray(erpRes.data.data) ? erpRes.data.data : [])
                : [];

            // Extract BCP plans with proper validation
            const bcpPlans = (bcpRes.data?.resCode === 200 && bcpRes.data?.data)
                ? (Array.isArray(bcpRes.data.data) ? bcpRes.data.data : [])
                : [];
            setOptions({
                users,
                roles,
                premises,
                erpPlans,
                bcpPlans
            });
        } catch (error) {
            console.error('Error fetching options:', error);
        }
    };

    const handleChannelToggle = (channel: string) => {
        setFormData((prev: any) => ({
            ...prev,
            channels: prev.channels.includes(channel)
                ? prev.channels.filter((c: any) => c !== channel)
                : [...prev.channels, channel]
        }));
    };

    const addUniqueValue = (field: string, value: string) => {
        if (!value) return;
        setFormData((prev: any) => ({
            ...prev,
            [field]: prev[field].includes(value) ? prev[field] : [...prev[field], value]
        }));
    };

    const removeValue = (field: string, value: string) => {
        setFormData((prev: any) => ({
            ...prev,
            [field]: prev[field].filter((item: string) => item !== value)
        }));
    };

    const getRoleLabel = (roleId: string) => {
        const role = options.roles.find((item: any) => item._id === roleId);
        return role ? (role.roleName || role.name || roleId) : roleId;
    };

    const getLocationLabel = (locationId: string) => {
        const premise = options.premises.find((item: any) => item._id === locationId);
        return premise ? (premise.premisesName || premise.name || locationId) : locationId;
    };

    const getUserLabel = (userId: string) => {
        const user = options.users.find((item: any) => item._id === userId);
        if (!user) return userId;
        const displayName = user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : user.name || user.email || 'Unknown User';
        return `${displayName} (${user.email || 'No email'})`;
    };

    const selectionBoxStyle = {
        border: '1px solid #bfdbfe',
        borderRadius: '8px',
        padding: '8px',
        backgroundColor: '#f8fbff'
    };

    const renderSelectedChips = (
        values: string[],
        field: string,
        getLabel: (id: string) => string
    ) => {
        if (!values.length) return null;
        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                {values.map((value) => (
                    <span
                        key={value}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            backgroundColor: '#dbeafe',
                            color: '#1e3a8a',
                            borderRadius: '9999px',
                            padding: '4px 10px',
                            fontSize: '12px',
                            maxWidth: '100%'
                        }}
                    >
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {getLabel(value)}
                        </span>
                        <button
                            type="button"
                            onClick={() => removeValue(field, value)}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                color: '#1e3a8a',
                                cursor: 'pointer',
                                fontSize: '14px',
                                lineHeight: 1,
                                padding: 0
                            }}
                            aria-label={`Remove ${getLabel(value)}`}
                        >
                            ×
                        </button>
                    </span>
                ))}
            </div>
        );
    };

    const renderSelectWithChips = (
        values: string[],
        field: string,
        getLabel: (id: string) => string,
        pickerValue: string,
        setPickerValue: (value: string) => void,
        placeholder: string,
        optionElements: React.ReactNode
    ) => (
        <div style={selectionBoxStyle}>
            {renderSelectedChips(values, field, getLabel)}
            <select
                value={pickerValue}
                onChange={(e) => {
                    addUniqueValue(field, e.target.value);
                    setPickerValue('');
                }}
                style={{ ...inputStyle, borderColor: '#93c5fd' }}
            >
                <option value="">{placeholder}</option>
                {optionElements}
            </select>
        </div>
    );

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.title || !formData.message || formData.channels.length === 0) {
            alert('Please fill in all required fields');
            return;
        }

        // Validate target selection based on targetType
        if (formData.targetType === 'Role' && formData.targetRoles.length === 0) {
            alert('Please select at least one role');
            return;
        }
        if (formData.targetType === 'Location' && formData.targetLocations.length === 0) {
            alert('Please select at least one location');
            return;
        }
        if (formData.targetType === 'User' && formData.targetUsers.length === 0) {
            alert('Please select at least one user');
            return;
        }

        try {
            setLoading(true);

            // Build payload aligned with EmergencyNotificationController
            const payload: any = {
                title: formData.title,
                message: formData.message,
                priority: formData.priority,
                channels: formData.channels,
                targetType: formData.targetType
            };

            // Resolve targets based on targetType
            if (formData.targetType === 'User' && formData.targetUsers.length > 0) {
                payload.targetUsers = formData.targetUsers;
            } else if (formData.targetType === 'Role' && formData.targetRoles.length > 0) {
                payload.targetRoles = formData.targetRoles;
            } else if (formData.targetType === 'Location' && formData.targetLocations.length > 0) {
                payload.targetLocations = formData.targetLocations;
            }

            // Only include linked IDs if they have values
            if (formData.linkedERPId) {
                payload.linkedERPId = formData.linkedERPId;
            }
            if (formData.linkedBCPId) {
                payload.linkedBCPId = formData.linkedBCPId;
            }

            // Only include escalation rules if enabled
            if (formData.escalationEnabled) {
                if (formData.escalateToUsers.length === 0) {
                    alert('Please select at least one user for escalation');
                    setLoading(false);
                    return;
                }
                payload.escalationRules = {
                    enabled: true,
                    timeoutMinutes: formData.escalationTimeout,
                    escalateTo: formData.escalateToUsers
                };
            }
            const response = await sendEmergencyNotification(payload);

            if (response.data.success) {
                alert('Emergency notification sent successfully!');
                navigate('/external-emergency-preparedness/notifications');
            } else {
                const errorMsg = response.data.resMessage || response.data.message || 'Failed to send notification';
                alert(`Error: ${errorMsg}`);
            }
        } catch (error: any) {
            console.error('Error sending notification:', error);
            console.error('Error response:', error.response?.data);
            
            // Extract error message from response
            const errorMsg = error.response?.data?.resMessage 
                || error.response?.data?.message 
                || error.response?.data?.error
                || error.message 
                || 'Failed to send notification. Please check the console for details.';
            
            alert(`Error: ${errorMsg}`);
        } finally {
            setLoading(false);
        }
    };

    const inputStyle = {
        width: '100%',
        padding: '10px',
        borderRadius: '6px',
        border: '1px solid #d1d5db',
        fontSize: '14px'
    };

    const labelStyle = {
        display: 'block',
        marginBottom: '6px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#374151'
    };

    return (
        <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ marginBottom: '24px' }}>
                <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
                    Send Emergency Notification
                </h1>
                <p style={{ color: '#6b7280', fontSize: '14px' }}>
                    Broadcast critical information to your team across multiple channels
                </p>
            </div>

            <form onSubmit={handleSubmit} style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                {/* Title */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={labelStyle}>
                        Title <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="e.g., Fire Drill - Building A"
                        style={inputStyle}
                        required
                    />
                </div>

                {/* Message */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={labelStyle}>
                        Message <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <textarea
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Enter your emergency message..."
                        rows={4}
                        style={{ ...inputStyle, resize: 'vertical' }}
                        required
                    />
                </div>

                {/* Priority */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={labelStyle}>Priority</label>
                    <select
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                        style={inputStyle}
                    >
                        <option value="Critical">Critical</option>
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                    </select>
                </div>

                {/* Channels */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={labelStyle}>
                        Channels <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <div style={{ display: 'flex', gap: '12px' }}>
                        {['SMS', 'Email', 'Push'].map(channel => (
                            <label key={channel} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={formData.channels.includes(channel)}
                                    onChange={() => handleChannelToggle(channel)}
                                    style={{ marginRight: '6px' }}
                                />
                                <span style={{ fontSize: '14px' }}>{channel}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Target Type */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={labelStyle}>Target Type</label>
                    <select
                        value={formData.targetType}
                        onChange={(e) => setFormData({ ...formData, targetType: e.target.value })}
                        style={inputStyle}
                    >
                        <option value="All">All Users</option>
                        <option value="Role">By Role</option>
                        <option value="Location">By Location</option>
                        <option value="User">Specific Users</option>
                    </select>
                </div>

                {/* Conditional Target Selection */}
                {formData.targetType === 'Role' && (
                    <div style={{ marginBottom: '20px' }}>
                        <label style={labelStyle}>Select Roles</label>
                        {renderSelectWithChips(
                            formData.targetRoles,
                            'targetRoles',
                            getRoleLabel,
                            rolePickerValue,
                            setRolePickerValue,
                            'Select role...',
                            options.roles.map((role: any) => (
                                <option key={role._id} value={role._id}>{role.roleName || role.name}</option>
                            ))
                        )}
                    </div>
                )}

                {formData.targetType === 'Location' && (
                    <div style={{ marginBottom: '20px' }}>
                        <label style={labelStyle}>Select Locations</label>
                        {renderSelectWithChips(
                            formData.targetLocations,
                            'targetLocations',
                            getLocationLabel,
                            locationPickerValue,
                            setLocationPickerValue,
                            'Select location...',
                            options.premises.map((premise: any) => (
                                <option key={premise._id} value={premise._id}>{premise.premisesName || premise.name}</option>
                            ))
                        )}
                    </div>
                )}

                {formData.targetType === 'User' && (
                    <div style={{ marginBottom: '20px' }}>
                        <label style={labelStyle}>Select Users</label>
                        {renderSelectWithChips(
                            formData.targetUsers,
                            'targetUsers',
                            getUserLabel,
                            userPickerValue,
                            setUserPickerValue,
                            'Select user...',
                            options.users.map((user: any) => (
                                <option key={user._id} value={user._id}>
                                    {user.firstName && user.lastName 
                                        ? `${user.firstName} ${user.lastName}` 
                                        : user.name || user.email || 'Unknown User'
                                    } ({user.email || 'No email'})
                                </option>
                            ))
                        )}
                    </div>
                )}

                {/* Link to Plans */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                    <div>
                        <label style={labelStyle}>Link to ERP (Optional)</label>
                        <select
                            value={formData.linkedERPId}
                            onChange={(e) => setFormData({ ...formData, linkedERPId: e.target.value })}
                            style={inputStyle}
                        >
                            <option value="">None</option>
                            {options.erpPlans.map((plan: any) => (
                                <option key={plan._id} value={plan._id}>{plan.erpName || plan.planName}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label style={labelStyle}>Link to BCP (Optional)</label>
                        <select
                            value={formData.linkedBCPId}
                            onChange={(e) => setFormData({ ...formData, linkedBCPId: e.target.value })}
                            style={inputStyle}
                        >
                            <option value="">None</option>
                            {options.bcpPlans.map((plan: any) => (
                                <option key={plan._id} value={plan._id}>{plan.planName}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Escalation Rules */}
                <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '12px' }}>
                        <input
                            type="checkbox"
                            checked={formData.escalationEnabled}
                            onChange={(e) => setFormData({ ...formData, escalationEnabled: e.target.checked })}
                            style={{ marginRight: '8px' }}
                        />
                        <span style={{ fontSize: '14px', fontWeight: '500' }}>Enable Escalation</span>
                    </label>

                    {formData.escalationEnabled && (
                        <>
                            <div style={{ marginBottom: '12px' }}>
                                <label style={labelStyle}>Escalation Timeout (minutes)</label>
                                <input
                                    type="number"
                                    value={formData.escalationTimeout}
                                    onChange={(e) => setFormData({ ...formData, escalationTimeout: parseInt(e.target.value) })}
                                    min="5"
                                    style={inputStyle}
                                />
                            </div>

                            <div>
                                <label style={labelStyle}>Escalate To</label>
                                {renderSelectWithChips(
                                    formData.escalateToUsers,
                                    'escalateToUsers',
                                    getUserLabel,
                                    escalationUserPickerValue,
                                    setEscalationUserPickerValue,
                                    'Select escalation user...',
                                    options.users.map((user: any) => (
                                        <option key={user._id} value={user._id}>
                                            {user.firstName && user.lastName 
                                                ? `${user.firstName} ${user.lastName}` 
                                                : user.name || user.email || 'Unknown User'
                                            } ({user.email || 'No email'})
                                        </option>
                                    ))
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button
                        type="button"
                        onClick={() => navigate('/external-emergency-preparedness/notifications')}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: '#e5e7eb',
                            color: '#374151',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '500'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: loading ? '#9ca3af' : '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontWeight: '500'
                        }}
                    >
                        {loading ? 'Sending...' : 'Send Notification'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default SendNotification;
